import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["focusInputRef", "blurInputRef", "onBlur", "onFocus", "onInputChange", "hasMultipleRows", "setHasMultipleRows", "value", "buttonWidth", "onCompositionStart", "onCompositionEnd"];
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';
import { useSetInputRef } from '../PromptForm/useSetInputRef';
import { hiddenDivStyles, inputStyles, multilineInputWrapperStyles } from './styles';
export var PromptMultilineInput = function PromptMultilineInput(_ref) {
  var focusInputRef = _ref.focusInputRef,
    blurInputRef = _ref.blurInputRef,
    onBlur = _ref.onBlur,
    onFocus = _ref.onFocus,
    onInputChange = _ref.onInputChange,
    hasMultipleRows = _ref.hasMultipleRows,
    setHasMultipleRows = _ref.setHasMultipleRows,
    value = _ref.value,
    buttonWidth = _ref.buttonWidth,
    onCompositionStart = _ref.onCompositionStart,
    onCompositionEnd = _ref.onCompositionEnd,
    rest = _objectWithoutProperties(_ref, _excluded);
  var setFocusAndBlurInputRef = useSetInputRef({
    blurInputRef: blurInputRef,
    focusInputRef: focusInputRef
  });
  var wrapperRef = useRef(null);
  var hiddenDivRef = useRef(null);
  var hiddenDiv = hiddenDivRef === null || hiddenDivRef === void 0 ? void 0 : hiddenDivRef.current;
  var singleLineHeight = useMemo(function () {
    return window && hiddenDiv ? parseFloat(window.getComputedStyle(hiddenDiv).lineHeight) : 0;
  }, [hiddenDiv]);
  var hiddenDivWidthStyles = useMemo(function () {
    return {
      width: hasMultipleRows ? "calc(100% - ".concat(buttonWidth, "px)") : "100%"
    };
  }, [hasMultipleRows, buttonWidth]);
  var onInput = useCallback(function (e) {
    onInputChange === null || onInputChange === void 0 || onInputChange(e.target.value);
  }, [onInputChange]);

  /**
   * Use resize observer to listen to changes in height of hidden div
   * And update hasMultipleRows state if content spans multiple rows
   * Note: we are listening to changes in a hidden div (with fixed width)
   * because when the horizontal width of textarea increases,
   * it causes flickering as the content only fits in one line if buttons are stacked
   */

  useEffect(function () {
    var resizeObserver = new ResizeObserver(function (entries) {
      entries.forEach(function () {
        if (hiddenDiv) {
          setHasMultipleRows === null || setHasMultipleRows === void 0 || setHasMultipleRows(hiddenDiv.getBoundingClientRect().height > singleLineHeight);
        }
      });
    });
    if (hiddenDiv) {
      resizeObserver.observe(hiddenDiv);
    }
    return function () {
      resizeObserver.disconnect();
    };
  }, [hiddenDiv, singleLineHeight, setHasMultipleRows]);
  useEffect(function () {
    if (hiddenDiv) {
      setHasMultipleRows === null || setHasMultipleRows === void 0 || setHasMultipleRows(hiddenDiv.getBoundingClientRect().height > singleLineHeight);
    }
  }, [hiddenDiv, singleLineHeight, setHasMultipleRows]);
  return (
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
    jsx("div", {
      "data-replicated-value": value,
      css: [multilineInputWrapperStyles],
      ref: wrapperRef
    }, jsx("textarea", _extends({
      ref: setFocusAndBlurInputRef,
      "aria-label": rest.placeholder,
      "data-testid": "ai-experience-prompt-input",
      name: "prompt-input",
      autoComplete: "off"
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
      ,
      css: inputStyles,
      onChange: onInput,
      onBlur: onBlur,
      onFocus: onFocus,
      rows: 1,
      value: value
      // Setting Arbitrary limit to 4000 for now but will further monitor user feedback
      ,
      maxLength: 4000,
      onCompositionStart: onCompositionStart,
      onCompositionEnd: onCompositionEnd
    }, rest)), jsx("div", {
      "data-replicated-value": value
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
      ,
      css: [hiddenDivStyles, hiddenDivWidthStyles],
      ref: hiddenDivRef
    }))
  );
};